import { useEffect, useState } from "react";
import { Cookie, X, Settings as Cog } from "lucide-react";

/**
 * PIPEDA + GDPR-aware cookie consent banner that wires into Google's
 * Consent Mode v2.
 *
 * Behaviour:
 *   - On first visit, blocks GA4 + Clarity analytics cookies via
 *     gtag('consent', 'default', { ... 'denied' ... }) (already set in
 *     index.html before this script even loads).
 *   - Banner appears bottom-right. "Accept all" → grants analytics_storage,
 *     ad_storage, etc. "Reject" → leaves them denied (but page still loads,
 *     pixel firing is suppressed). "Customize" → granular toggles.
 *   - Choice persisted in localStorage as 'tc_cookie_consent_v1' with
 *     timestamp. Re-prompt every 365 days.
 *   - Hidden on /admin and /checkout (legitimate-interest contexts).
 */

const KEY = "tc_cookie_consent_v1";
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const HIDE_PREFIXES = ["/admin", "/checkout"];

function updateConsent(analytics, marketing) {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
    });
  } catch {
    /* no-op */
  }
}

function loadStored() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp) return null;
    if (Date.now() - parsed.timestamp > ONE_YEAR_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [show, setShow] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (HIDE_PREFIXES.some((p) => window.location.pathname.startsWith(p))) return;
    const stored = loadStored();
    if (stored) {
      // Re-apply stored choice silently so gtag knows on every page load.
      updateConsent(!!stored.analytics, !!stored.marketing);
      return;
    }
    // First visit — show banner after slight delay to avoid CLS competing with hero.
    const t = setTimeout(() => setShow(true), 800);
    return () => clearTimeout(t);
  }, []);

  const persist = (a, m) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        analytics: a,
        marketing: m,
        timestamp: Date.now(),
        version: 1,
      }));
    } catch { /* no-op */ }
    updateConsent(a, m);
    setShow(false);
  };

  const acceptAll = () => persist(true, true);
  const rejectAll = () => persist(false, false);
  const saveCustom = () => persist(analytics, marketing);

  if (!show) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 right-4 left-4 md:left-auto md:right-6 md:bottom-6 z-[60] max-w-md surface-card p-5 shadow-2xl"
      role="dialog"
      aria-labelledby="cookie-banner-title"
    >
      <button
        onClick={rejectAll}
        aria-label="Close cookie banner"
        data-testid="cookie-banner-close"
        className="absolute top-3 right-3 text-zinc-500 hover:text-white"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-3 mb-3">
        <Cookie size={22} className="text-[var(--tc-red)] flex-shrink-0 mt-0.5" />
        <h3 id="cookie-banner-title" className="font-display text-xl uppercase leading-tight">
          We use cookies
        </h3>
      </div>

      <p className="font-body text-sm text-zinc-300 mb-4 leading-relaxed">
        We use essential cookies to make our site work, and analytics cookies (Google
        Analytics, Microsoft Clarity) to understand how visitors use it.{" "}
        <a href="/policy/privacy" className="underline hover:text-white">
          Privacy Policy
        </a>
        .
      </p>

      {customize && (
        <div data-testid="cookie-customize-panel" className="space-y-3 mb-4 border-t border-[#222] pt-4">
          <label className="flex items-start gap-3 cursor-not-allowed opacity-70">
            <input
              type="checkbox"
              checked
              disabled
              className="mt-1"
              aria-label="Essential cookies (required)"
            />
            <span className="text-sm">
              <span className="font-ui font-bold block">Essential</span>
              <span className="text-zinc-400 text-xs">Required for cart, login, checkout. Always on.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              data-testid="cookie-toggle-analytics"
              className="mt-1"
            />
            <span className="text-sm">
              <span className="font-ui font-bold block">Analytics</span>
              <span className="text-zinc-400 text-xs">Google Analytics + Microsoft Clarity.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              data-testid="cookie-toggle-marketing"
              className="mt-1"
            />
            <span className="text-sm">
              <span className="font-ui font-bold block">Marketing</span>
              <span className="text-zinc-400 text-xs">Ad personalization (currently unused).</span>
            </span>
          </label>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        {customize ? (
          <button
            onClick={saveCustom}
            data-testid="cookie-save-custom"
            className="flex-1 btn-tc-primary text-xs py-3"
          >
            Save preferences
          </button>
        ) : (
          <>
            <button
              onClick={acceptAll}
              data-testid="cookie-accept-all"
              className="flex-1 btn-tc-primary text-xs py-3"
            >
              Accept all
            </button>
            <button
              onClick={rejectAll}
              data-testid="cookie-reject-all"
              className="flex-1 btn-tc-secondary text-xs py-3"
            >
              Reject
            </button>
          </>
        )}
        {!customize && (
          <button
            onClick={() => setCustomize(true)}
            data-testid="cookie-customize-toggle"
            className="inline-flex items-center justify-center gap-1 font-mono text-[10px] tracking-widest uppercase text-zinc-400 hover:text-white py-3 px-3"
          >
            <Cog size={12} /> Customize
          </button>
        )}
      </div>
    </div>
  );
}
